import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Flowers.css";
import LazyImage from "./LazyImage";
import SortSelector from "./SortSelector";
import "../styles/AvailabilityPopup.css";
import AvailabilityModal from "./AvailabilityPopup";

const FlowerContainer = ({
  flowers,
  title,
  subtitle = null,
  storageKey = "FLOWER_SCROLL_POSITION",
}) => {
  const navigate = useNavigate();
  const [sortedFlowers, setSortedFlowers] = useState([]);
  const [sortOption, setSortOption] = useState("default");
  const [selectedFlower, setSelectedFlower] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Use useCallback to memoize these functions so they don't cause unnecessary re-renders
  const saveScrollPosition = useCallback(() => {
    const data = {
      position: window.scrollY,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(storageKey, JSON.stringify(data));
  }, [storageKey]);

  // Get scroll position with useCallback
  const getScrollPosition = useCallback(() => {
    const data = sessionStorage.getItem(storageKey);
    if (!data) return null;

    try {
      return JSON.parse(data);
    } catch (e) {
      return null;
    }
  }, [storageKey]);

  // Initial setup and scroll restoration
  useEffect(() => {
    const scrollData = getScrollPosition();

    if (scrollData) {
      const timeSinceLastVisit = Date.now() - scrollData.timestamp;
      if (timeSinceLastVisit < 5000) {
        setTimeout(() => {
          window.scrollTo(0, scrollData.position);
        }, 200);
      } else {
        window.scrollTo(0, 0);
      }
    }

    // Initialize sorted flowers
    setSortedFlowers([...flowers]);
  }, [flowers, getScrollPosition]);

  // Handle sorting
  useEffect(() => {
    if (!flowers || flowers.length === 0) {
      setSortedFlowers([]);
      return;
    }

    const flowersCopy = [...flowers];

    switch (sortOption) {
      case "price-asc":
        flowersCopy.sort((a, b) => {
          const priceA = parseFloat(
            a.price.replace(/[^\d,.]/g, "").replace(",", ".")
          );
          const priceB = parseFloat(
            b.price.replace(/[^\d,.]/g, "").replace(",", ".")
          );
          return priceA - priceB;
        });
        break;

      case "price-desc":
        flowersCopy.sort((a, b) => {
          const priceA = parseFloat(
            a.price.replace(/[^\d,.]/g, "").replace(",", ".")
          );
          const priceB = parseFloat(
            b.price.replace(/[^\d,.]/g, "").replace(",", ".")
          );
          return priceB - priceA;
        });
        break;

      default:
        // No sorting changes
        break;
    }

    setSortedFlowers(flowersCopy);
  }, [flowers, sortOption]);

  // Sort change handler
  const handleSortChange = (option) => {
    setSortOption(option);
  };

  // Navigate to flower detail page
  const handleFlowerClick = useCallback(
    (flowerId) => {
      saveScrollPosition();
      navigate(`/flower/${flowerId}`);
    },
    [navigate, saveScrollPosition]
  );

  // Handle availability check - open modal
  const handleAvailabilityCheck = useCallback((flower, e) => {
    e.stopPropagation();
    setSelectedFlower(flower);
    setShowModal(true);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = "hidden";
  }, []);

  // Close modal
  const closeModal = useCallback(() => {
    setShowModal(false);
    setSelectedFlower(null);
    // Restore body scrolling
    document.body.style.overflow = "auto";
  }, []);

  return (
    <div className="section flower-section">
      <h1 className="heading-secondary">{title}</h1>

      {subtitle && <p className="subheading">{subtitle}</p>}

      <SortSelector onSortChange={handleSortChange} currentSort={sortOption} />

      <div className="flower-grid">
        {sortedFlowers.map((flower) => (
          <div
            key={flower.id}
            className="flower-card"
            onClick={
              flower.checkAvailability
                ? (e) => handleAvailabilityCheck(flower, e)
                : () => handleFlowerClick(flower.id)
            }
          >
            <LazyImage src={flower.images[0]} alt={flower.name} />
            <h3 className="flowerName">{flower.name}</h3>
            <p className="flowerPrice">{flower.price}</p>

            {/* Conditionally render the button based on availability flag */}
            {flower.checkAvailability ? (
              <button
                className="btn checkAvailability buyNow"
                onClick={(e) => handleAvailabilityCheck(flower, e)}
              >
                Verificați disponibilitatea
              </button>
            ) : (
              <button
                className="btn buyNow"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFlowerClick(flower.id);
                }}
              >
                Comanda acum
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Render modal when showModal is true */}
      {showModal && (
        <AvailabilityModal flower={selectedFlower} onClose={closeModal} />
      )}
    </div>
  );
};

export default FlowerContainer;
